import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { C, FU } from "./tokens";

/**
 * Section shell — wraps a section's rows and renders the right-rail "On this
 * page" outline. The outline scans its own DOM for `[data-row-anchor]`
 * elements emitted by <Row>, so authors don't have to maintain two lists.
 *
 * Layout:
 *   ┌────────────────────────────────────────┐
 *   │  SectionHeader (passed as `header`)    │
 *   │                                        │
 *   │   ┌──────────────────────┐  ┌───────┐  │
 *   │   │ rows (children)      │  │ TOC   │  │
 *   │   │                      │  │       │  │
 *   │   └──────────────────────┘  └───────┘  │
 *   └────────────────────────────────────────┘
 *
 * Below ~1200px the rail tucks away and only the rows remain — same content,
 * narrower viewport.
 */
/**
 * Rail display mode:
 *   "collapsible" — default (try1). 32px column of marks; hovering or
 *                   keyboard-focusing pops out a 240px overlay panel.
 *   "always-open" — try3. Permanent 200px column, editorial text-only list
 *                   with "On this page" heading. No marks, no hover popout.
 *   "minimal"     — try4 minimal state. 56px column, horizontal tick marks
 *                   only (Apple Pages style). Variant-based widths. Active
 *                   = gold. Hover = enlarge + darken. No text.
 *   "full"        — try4 full state. 200px column. Ticks (same vocabulary
 *                   as "minimal") + numbers + labels next to them. No
 *                   heading. The minimal-to-full transition is just "labels
 *                   appear next to existing ticks", so the two states feel
 *                   like one design at different widths.
 */
type RailMode = "collapsible" | "always-open" | "minimal" | "full" | "none";

interface SectionShellProps {
  header: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Section prefix used to compose hierarchical numbers in the right-rail
   * outline (e.g. "2.1" → rows become "2.1.1", "2.1.2", …). Omit for the
   * default zero-padded numbering ("01", "02", …).
   */
  sectionNumber?: string;
  /** See `RailMode`. Defaults to "collapsible". */
  railMode?: RailMode;
  /**
   * Optional callback wired to a panel-toggle button rendered at the top of
   * the right rail. Use this for sandboxes where the rail's state is paired
   * with another state (e.g. /try4 toggles the left sidebar's collapsed
   * state). When unset, no toggle button is rendered.
   */
  onRailToggle?: () => void;
}

interface AnchorEntry {
  id: string;
  label: string;
  variant: string;
}

export function SectionShell({
  header,
  children,
  footer,
  sectionNumber,
  railMode = "collapsible",
  onRailToggle,
}: SectionShellProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [entries, setEntries] = useState<AnchorEntry[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  // When the user clicks a rail button we trigger a smooth scroll. During
  // that scroll the IntersectionObserver fires repeatedly as intermediate
  // rows pass through the active band, which can leave the highlight on a
  // row the user didn't click. We set this flag for ~800ms after a click
  // and ignore observer updates during the window — the click's intended
  // active state stays locked. A ref (not state) so flipping it doesn't
  // re-render and tear down the observer.
  const suppressObserverRef = useRef(false);
  const suppressTimerRef = useRef<number | null>(null);

  // Collect anchored rows after mount. We re-collect on resize because some
  // rows are only mounted at certain breakpoints, and on a small mutation
  // observer for the section body (sections re-render when toggles flip).
  useEffect(() => {
    const collect = () => {
      const body = bodyRef.current;
      if (!body) return;
      const nodes = body.querySelectorAll<HTMLElement>("[data-row-anchor]");
      const next: AnchorEntry[] = [];
      for (const n of nodes) {
        const id = n.getAttribute("data-row-anchor");
        const label = n.getAttribute("data-row-label");
        const variant = n.getAttribute("data-row-variant") ?? "standard";
        if (id && label) next.push({ id, label, variant });
      }
      setEntries(next);
    };
    collect();
    const obs = new MutationObserver(collect);
    if (bodyRef.current) obs.observe(bodyRef.current, { childList: true, subtree: true });
    window.addEventListener("resize", collect);
    return () => {
      obs.disconnect();
      window.removeEventListener("resize", collect);
    };
  }, []);

  // Scroll-spy: which row is "active" right now? Pick whichever crosses the
  // 20% line from the viewport top — standard offset for outline highlighters.
  // `entries` is a re-run trigger: when the collected anchors change we must
  // re-observe the new DOM nodes, even though it isn't read in the body.
  // biome-ignore lint/correctness/useExhaustiveDependencies: entries triggers re-observe
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Ignore observer firings while a programmatic smooth scroll is in
        // flight — the click handler has already locked the correct active
        // state and we don't want intermediate rows hijacking it.
        if (suppressObserverRef.current) return;
        // Prefer the topmost intersecting entry — gives the most stable
        // highlight when several rows overlap the viewport (e.g. on a tall
        // monitor where 3 rows are visible at once).
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const top = visible[0];
        if (top) {
          const id = (top.target as HTMLElement).getAttribute("data-row-anchor");
          if (id) setActiveId(id);
        }
      },
      {
        rootMargin: "-20% 0px -55% 0px",
        threshold: 0,
      },
    );
    for (const n of body.querySelectorAll<HTMLElement>("[data-row-anchor]")) observer.observe(n);
    return () => observer.disconnect();
  }, [entries]);

  // Cleanup any pending suppression timer when the shell unmounts.
  useEffect(() => {
    return () => {
      if (suppressTimerRef.current !== null) {
        window.clearTimeout(suppressTimerRef.current);
      }
    };
  }, []);

  const jumpTo = (id: string) => {
    // Scope the lookup to this section's body — `document.getElementById`
    // would return the FIRST element with that id document-wide, which is
    // wrong when two sections (or the page chrome) happen to share an
    // anchor name. querySelector within bodyRef guarantees we scroll to
    // the row inside this shell's own scope.
    const body = bodyRef.current;
    const el = body?.querySelector<HTMLElement>(`[data-row-anchor="${CSS.escape(id)}"]`);
    if (!el) return;
    // Lock the active state to the clicked id immediately so the rail
    // reflects the user's intent at click time. The observer would
    // otherwise wait for the scroll to settle, which on long jumps lets
    // intermediate rows flicker through as "active".
    setActiveId(id);
    suppressObserverRef.current = true;
    if (suppressTimerRef.current !== null) {
      window.clearTimeout(suppressTimerRef.current);
    }
    // 800ms covers a typical smooth-scroll duration on most viewports.
    // After the window, the observer takes over again so user-driven
    // scrolling continues to update the active state normally.
    suppressTimerRef.current = window.setTimeout(() => {
      suppressObserverRef.current = false;
      suppressTimerRef.current = null;
    }, 800);
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="trysec-wrap">
      {header}
      <div className="trysec-grid" data-rail-mode={railMode}>
        <div className="trysec-body" ref={bodyRef}>
          {children}
          {footer && <div style={{ marginTop: 8 }}>{footer}</div>}
        </div>

        {/* Right rail — Notion-style outline. Default: ~32px column of short
            marks (length and prominence vary by row variant — hero biggest,
            compact smallest). On hover or keyboard focus the rail pops out
            leftward into a ~220px panel showing hierarchical numbers and
            labels. The grid reserves a constant 32px column so the content
            never shifts when the rail expands — the expanded panel overlays
            content instead of pushing it.
            Hidden under 1200px (mobile/tablet). */}
        {railMode !== "none" && (
          <aside className="trysec-rail" data-mode={railMode} aria-label="On this page">
            <div className="trysec-rail-inner">
              {/* Optional toggle button — rendered only when onRailToggle is
                provided (typically /try4, where the right rail's state is
                paired with the left sidebar's). Same panel-toggle icon as
                the sidebar so the two toggles read as a matched pair. */}
              {onRailToggle && (
                <button
                  type="button"
                  className="trysec-rail-toggle"
                  onClick={onRailToggle}
                  aria-label="Toggle outline rail"
                  title="Toggle outline rail"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <rect x="2.5" y="3.5" width="11" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                    <line x1="10" y1="3.5" x2="10" y2="12.5" stroke="currentColor" strokeWidth="1.4" />
                  </svg>
                </button>
              )}
              {/* Heading — shown only in always-open mode. The collapsible mode
                drops it because the popout is transient and self-explanatory
                (you opened it on purpose). Always-open is a permanent UI
                element and needs a label so first-time users know what it is. */}
              {railMode === "always-open" && entries.length > 0 && (
                <div className="trysec-rail-heading">On this page</div>
              )}
              {entries.length === 0 ? null : (
                <ol className="trysec-rail-list">
                  {entries.map((e, i) => {
                    const isActive = e.id === activeId;
                    // Hierarchical number: "2.1" + ".1" → "2.1.1". Falls back
                    // to a zero-padded counter when no section number is set,
                    // so authors who don't care about numbering still get
                    // something readable.
                    const num = sectionNumber ? `${sectionNumber}.${i + 1}` : String(i + 1).padStart(2, "0");
                    return (
                      <li key={e.id}>
                        <button
                          type="button"
                          onClick={() => jumpTo(e.id)}
                          data-active={isActive}
                          data-variant={e.variant}
                          className="trysec-rail-btn"
                          // Native tooltip — useful when only the tick mark is
                          // visible (minimal mode); harmless in other modes.
                          title={`${num} · ${e.label}`}
                        >
                          <span className="trysec-rail-mark" aria-hidden="true" />
                          <span className="trysec-rail-num">{num}</span>
                          <span className="trysec-rail-label">{e.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </aside>
        )}
      </div>

      <style>{SHELL_STYLES}</style>
    </div>
  );
}

/**
 * Static stylesheet for the section shell. Hoisted to module scope so the
 * ~540-line CSS string is built once at module load rather than re-created on
 * every render — and SectionShell re-renders on every scroll-spy activeId
 * change. All interpolations are static design tokens (`C`, `FU`).
 */
const SHELL_STYLES = `
        /* ─── TQ design-system constraints applied here ─────────────────────
           · Spacing snapped to 4/8/12/16/20/24/32
           · Font sizes snapped to 11/12/13/14 (no half-pixel values)
           · Max border-radius 6px ("sharp and professional" — dashboard rule)
           · Motion: 180ms ease (matches --tq-motion-hover)
           · Focus: 2px gold outline with 2px offset (§2.2)
           · Tabular nums for numeric labels so 2.1.10 doesn't drift
        ──────────────────────────────────────────────────────────────────── */
        .trysec-wrap { width: 100%; }
        .trysec-grid {
          display: grid;
          /* Rail column is a constant 32px so the content well's width never
             changes when the rail expands. The expanded panel overlays the
             content (via margin-left on .trysec-rail-inner). */
          grid-template-columns: minmax(0, 1fr) 32px;
          gap: 32px;
          /* Animate the column-width change for /try4 minimal↔full so the
             rail's left edge slides outward smoothly instead of snapping. */
          transition: grid-template-columns 260ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        /* Rail-less sections (their own top-level nav replaces the outline) —
           single full-width column, no reserved rail gap. */
        .trysec-grid[data-rail-mode="none"] {
          grid-template-columns: minmax(0, 1fr);
          gap: 0;
        }
        .trysec-body { min-width: 0; }

        /* ── Rail container ──────────────────────────────────────────────── */
        .trysec-rail {
          position: sticky;
          /* Clears the sticky breadcrumb (≈54px tall) plus a small breath. */
          top: 84px;
          align-self: start;
          width: 32px;
          /* Inner needs to overflow leftward when hovered; rail must allow it. */
          overflow: visible;
          /* Above section content (dark-shell sections lift their content to
             z-index 1 over the fixed background layer — without this the
             collapsed ticks paint UNDER the cards), below the sticky
             breadcrumb (30) and the app sidebar (40). */
          z-index: 10;
          transition: width 260ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        /* ── Rail panel — one object at two widths (client redesign 2026-07-08).
           Collapsed at rest: a 32px column of right-anchored ticks. Expanded
           ONLY on hover / keyboard focus (no auto-reveal): the SAME column
           grows leftward to 240px; the ticks never move or vanish (they're
           pinned to the fixed right edge), the number + label materialise
           beside them. The panel's fill is the page background showing
           through (blurred page tint) — not a floating card: no border, no
           shadow. */
        .trysec-rail-inner {
          width: 32px;
          padding: 12px 0;
          font-family: ${FU};
          border-radius: 10px;
          background: transparent;
          transition: width 220ms cubic-bezier(0.22, 0.61, 0.36, 1),
                      padding 220ms cubic-bezier(0.22, 0.61, 0.36, 1),
                      margin-left 220ms cubic-bezier(0.22, 0.61, 0.36, 1),
                      background 220ms ease;
        }
        .trysec-rail[data-mode="collapsible"]:hover .trysec-rail-inner,
        .trysec-rail[data-mode="collapsible"]:focus-within .trysec-rail-inner {
          width: 240px;
          padding: 10px 6px;
          background: color-mix(in srgb, ${C.bg} 82%, transparent);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          margin-left: -208px;
          position: relative;
          z-index: 20;
        }

        .trysec-rail-empty {
          font-size: 12px;
          color: ${C.textLo};
          font-style: italic;
          padding: 4px;
        }

        /* ── List — tight gap between rows, Notion-density. */
        .trysec-rail-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: grid;
          gap: 2px;
        }

        /* ── Button — flex (not grid) so each child transitions width and
           opacity independently. */
        .trysec-rail-btn {
          display: flex;
          align-items: center;
          gap: 0;
          width: 100%;
          background: transparent;
          border: none;
          padding: 6px 8px;
          text-align: left;
          cursor: pointer;
          color: ${C.textMd};
          border-radius: 4px;
          transition: gap 180ms ease, color 180ms ease, background 180ms ease,
                      box-shadow 180ms ease;
        }
        /* Collapsible rows — relative so the tick can pin to the row's right
           edge (the shared, never-moving anchor of both states). The right
           padding reserves the tick strip; the gap is constant so expansion
           never reflows the row. */
        .trysec-rail[data-mode="collapsible"] .trysec-rail-btn {
          position: relative;
          padding: 6px 30px 6px 10px;
          gap: 10px;
        }

        /* Hover — uniform color shift + subtle bg lift. */
        .trysec-rail-btn:hover {
          color: ${C.textHi};
          background: var(--tq-mute);
        }
        .trysec-rail-btn:hover .trysec-rail-num,
        .trysec-rail-btn:hover .trysec-rail-label {
          color: ${C.textHi};
        }
        .trysec-rail-btn:focus-visible {
          outline: 2px solid ${C.goldText};
          outline-offset: 2px;
        }

        /* ── Mark — uniform 14×2 across all rows at rest (matches /try4
           minimal vocabulary). Variant-based widths and the active height
           bump were dropped per design feedback; only colour and length
           differentiate active from inactive now. Right-anchored via
           margin-left:auto so all marks terminate at the same x-coord. */
        .trysec-rail-mark {
          display: block;
          flex: 0 0 auto;
          width: 14px;
          height: 2px;
          border-radius: 1px;
          background: ${C.textLo};
          margin-left: auto;
          margin-right: 8px;
          transition: width 180ms ease, height 180ms ease, background 180ms ease,
                      opacity 180ms ease, margin 180ms ease;
        }
        .trysec-rail-btn[data-active="true"] .trysec-rail-mark {
          background: ${C.goldText};
          /* Active dash is ~21% longer (17 vs 14). The right edge is
             pinned by margin-left:auto, so the extra length extends
             leftward from the shared right anchor. */
          width: 17px;
        }
        /* Collapsible mode: the tick is pinned to the row's right edge in
           BOTH states — the panel grows around it, so collapsed → expanded
           reads as the same object growing, never a swap. */
        .trysec-rail[data-mode="collapsible"] .trysec-rail-mark {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          margin: 0;
        }

        /* ── Number — hierarchical "2.1.x". Weight 500 (not 700) so the
           number sits quietly behind the label rather than competing
           with it. Tabular-nums keeps digit columns aligned. */
        .trysec-rail-num {
          flex: 0 0 auto;
          width: 0;
          font-size: 11px;
          font-weight: 500;
          font-variant-numeric: tabular-nums;
          letter-spacing: 0;
          color: ${C.textLo};
          white-space: nowrap;
          opacity: 0;
          overflow: hidden;
          transition: width 180ms ease, opacity 160ms ease 60ms, color 180ms ease;
        }
        .trysec-rail[data-mode="collapsible"]:hover .trysec-rail-num,
        .trysec-rail[data-mode="collapsible"]:focus-within .trysec-rail-num {
          width: 36px;
          opacity: 1;
        }

        /* ── Label — single-line + ellipsis (not the previous 2-line
           clamp). Keeps every popout row to a uniform height; long
           labels truncate visually but stay discoverable via the
           button's title tooltip. */
        .trysec-rail-label {
          flex: 0 1 0;
          min-width: 0;
          width: 0;
          font-size: 13px;
          line-height: 1.4;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          opacity: 0;
          transition: flex 180ms ease, width 180ms ease, opacity 160ms ease 60ms,
                      color 180ms ease;
        }
        .trysec-rail[data-mode="collapsible"]:hover .trysec-rail-label,
        .trysec-rail[data-mode="collapsible"]:focus-within .trysec-rail-label {
          flex: 1 1 auto;
          width: auto;
          opacity: 1;
        }

        /* ── Active state — the accent (gold) carries the hierarchy: gold
           tick + gold number + bold high-contrast label. No row fill; the
           active row stays as calm as the rest of the panel. */
        .trysec-rail-btn[data-active="true"]                  { color: ${C.textHi}; }
        .trysec-rail-btn[data-active="true"] .trysec-rail-num { color: ${C.textHi}; }
        .trysec-rail-btn[data-active="true"] .trysec-rail-label {
          font-weight: 600;
          color: ${C.textHi};
        }
        .trysec-rail[data-mode="collapsible"] .trysec-rail-btn[data-active="true"] .trysec-rail-num {
          color: ${C.goldText};
        }

        /* ── Always-open mode — minimal editorial list ─────────────────────
           No container chrome, no dots, no timeline line. The rail reads
           as a plain numbered list — like a magazine's chapter index.
           Active state is conveyed entirely through TYPE: number + label
           shift from muted (textLo / textMd) to primary (textHi), label
           goes bold. Hover layers a faint bg + text-color shift from the
           universal rules. Generous vertical spacing carries the rest. */
        /* Rail column shrunk from 240 → 200 because items only need
           ~180px (10 padding + 36 num + 12 gap + label). The extra 40px
           was dead space on the right — visible as a gap between the
           label and the viewport edge. */
        .trysec-grid[data-rail-mode="always-open"] {
          grid-template-columns: minmax(0, 1fr) 200px;
        }
        .trysec-rail[data-mode="always-open"] {
          width: 200px;
          border-left: none;
          padding-left: 0;
          top: 96px;
        }
        .trysec-rail[data-mode="always-open"] .trysec-rail-inner {
          width: auto;
          padding: 0;
          background: transparent;
          border: none;
          box-shadow: none;
          border-radius: 0;
          margin-left: 0;
          position: static;
          z-index: auto;
        }
        /* Collapsed-state marks have no role here. */
        .trysec-rail[data-mode="always-open"] .trysec-rail-mark {
          display: none;
        }

        /* Heading — small uppercase eyebrow above the list. Same letter-
           spacing + weight vocabulary as the section kicker rules, so the
           rail's heading reads as part of the same typographic system.
           Padding-left matches the buttons (10px) so the text aligns. */
        .trysec-rail[data-mode="always-open"] .trysec-rail-heading {
          font-family: ${FU};
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: ${C.textMd};
          padding: 0 0 0 10px;
          margin-bottom: 12px;
        }

        /* Generous vertical breathing — items read as separate entries, not
           as rows in a table. */
        .trysec-rail[data-mode="always-open"] .trysec-rail-list {
          gap: 4px;
          padding: 0;
        }

        /* Buttons — no dot, no left rule. Padding only on left + vertical;
           the right side hugs the label since there's no element after it
           to space away from. Hover bg follows the same shape — narrower
           pill, no wasted right-edge area. */
        .trysec-rail[data-mode="always-open"] .trysec-rail-btn {
          padding: 10px 0 10px 10px;
          gap: 12px;
          border-radius: 4px;
          /* Top-align so the number stays on the label's first line when a
             long label wraps onto a second line. */
          align-items: flex-start;
        }

        /* Inactive number — lighter than the label so the row has its own
           micro-hierarchy. */
        .trysec-rail[data-mode="always-open"] .trysec-rail-num {
          width: 36px;
          opacity: 1;
          color: ${C.textLo};
        }
        .trysec-rail[data-mode="always-open"] .trysec-rail-label {
          flex: 1 1 auto;
          width: auto;
          opacity: 1;
          /* Always-open is a permanent column, so a long label should wrap
             onto the next line rather than truncate to an ellipsis that hides
             part of the section name. Overrides the base single-line rule. */
          white-space: normal;
          overflow: visible;
          text-overflow: clip;
          overflow-wrap: anywhere;
        }

        /* Active row — no bg, no inset rule, no dot. Type-only emphasis:
           number darkens, label goes bold + dark. The universal rules
           already supply most of this; the override here is just to
           neutralise the popout's mute-strong bg so the row stays clean. */
        .trysec-rail[data-mode="always-open"] .trysec-rail-btn[data-active="true"] {
          background: transparent;
          box-shadow: none;
        }
        .trysec-rail[data-mode="always-open"] .trysec-rail-btn[data-active="true"] .trysec-rail-num {
          color: ${C.textHi};
        }

        /* ── /try4 modes — minimal ↔ full (shared layout, smart-animate) ────
           Both modes share ONE layout so the browser can interpolate
           every visual difference frame-by-frame. The marker is
           position:absolute, pinned to each row's right edge — it does
           NOT move when modes flip, only its silhouette morphs (14×2
           dash with 1px radius → 4×4 dot with 50% radius). The flex
           children (number, label) sit in row-reverse flow, so on
           expansion they "emerge from the left" as the rail's left edge
           slides outward and space opens up beside the marker. Net
           effect: dash compacts into a dot in place, rail opens, text
           materialises next to it — all in one continuous motion.

           What changes between modes (all interpolable):
             · rail width            40px ↔ 200px
             · mark width            14   ↔ 4
             · mark height           2    ↔ 4
             · mark border-radius    1px  ↔ 50%
             · num width             0    ↔ 32
             · num opacity           0    ↔ 1
             · label opacity         0    ↔ 1
           What stays constant:
             · mark position (right: 10px from rail edge)
             · button padding, gap, flex-direction
             · rail position, sticky offset                            */

        .trysec-grid[data-rail-mode="minimal"] {
          grid-template-columns: minmax(0, 1fr) 40px;
        }
        .trysec-grid[data-rail-mode="full"] {
          grid-template-columns: minmax(0, 1fr) 200px;
        }
        .trysec-rail[data-mode="minimal"],
        .trysec-rail[data-mode="full"] {
          border-left: none;
          padding-left: 0;
          top: 96px;
        }
        .trysec-rail[data-mode="minimal"] { width: 40px; }
        .trysec-rail[data-mode="full"]    { width: 200px; }

        /* Strip popout chrome — both modes are flat (no floating panel). */
        .trysec-rail[data-mode="minimal"] .trysec-rail-inner,
        .trysec-rail[data-mode="full"] .trysec-rail-inner {
          width: auto;
          padding: 0;
          background: transparent;
          border: none;
          box-shadow: none;
          border-radius: 0;
          margin-left: 0;
          position: static;
          z-index: auto;
        }

        /* No heading in either /try4 mode. */
        .trysec-rail[data-mode="minimal"] .trysec-rail-heading,
        .trysec-rail[data-mode="full"] .trysec-rail-heading {
          display: none;
        }

        .trysec-rail[data-mode="minimal"] .trysec-rail-list,
        .trysec-rail[data-mode="full"] .trysec-rail-list {
          gap: 14px;
          padding: 0;
        }

        /* Button — relative positioning so the mark can anchor absolutely
           inside it. Default (row) direction puts the number on the
           visual left and the label after it, so the row reads naturally
           left-to-right as "[2.1.x] [label]…" with the mark pinned to
           the right edge. padding-right of 26px reserves the right strip
           for the absolute mark (14px dash + 10px from rail edge + 2px
           breath). */
        .trysec-rail[data-mode="minimal"] .trysec-rail-btn,
        .trysec-rail[data-mode="full"] .trysec-rail-btn {
          position: relative;
          display: flex;
          flex-direction: row;
          align-items: center;
          padding: 6px 26px 6px 10px;
          gap: 12px;
          border-radius: 4px;
          background: transparent;
        }
        /* Active row — no extra chrome; the gold mark + bold label carry it. */
        .trysec-rail[data-mode="minimal"] .trysec-rail-btn[data-active="true"],
        .trysec-rail[data-mode="full"] .trysec-rail-btn[data-active="true"] {
          background: transparent;
          box-shadow: none;
        }

        /* Marker — the SHARED ELEMENT. Absolutely positioned at the row's
           right edge in both modes; only its shape changes. Pinning it
           outside the flex flow is what makes the morph read as "the
           same object changing form" rather than "two different objects
           swapping in". The 280ms easing on width/height/radius is the
           heart of the smart-animate feel. */
        .trysec-rail[data-mode="minimal"] .trysec-rail-mark,
        .trysec-rail[data-mode="full"] .trysec-rail-mark {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          display: block;
          margin: 0;
          background: ${C.textLo};
          transition: width 280ms cubic-bezier(0.22, 1, 0.36, 1),
                      height 280ms cubic-bezier(0.22, 1, 0.36, 1),
                      border-radius 280ms cubic-bezier(0.22, 1, 0.36, 1),
                      background 180ms ease;
        }
        .trysec-rail[data-mode="minimal"] .trysec-rail-mark {
          width: 14px;
          height: 2px;
          border-radius: 1px;
        }
        .trysec-rail[data-mode="full"] .trysec-rail-mark {
          width: 4px;
          height: 4px;
          border-radius: 50%;
        }
        .trysec-rail[data-mode="minimal"] .trysec-rail-btn:hover .trysec-rail-mark,
        .trysec-rail[data-mode="full"] .trysec-rail-btn:hover .trysec-rail-mark {
          background: ${C.textMd};
        }
        /* Active mark = gold. Minimal also widens to 17px (~21% longer)
           so the active state reads at a glance on the narrow column.
           Full mode keeps the dot small — colour alone differentiates. */
        .trysec-rail[data-mode="minimal"] .trysec-rail-btn[data-active="true"] .trysec-rail-mark {
          background: ${C.goldText};
          width: 17px;
        }
        .trysec-rail[data-mode="full"] .trysec-rail-btn[data-active="true"] .trysec-rail-mark {
          background: ${C.goldText};
        }

        /* Number — quiet navigation reference. Width 0 + opacity 0 in
           minimal (transitionable, unlike display:none); width 32 +
           opacity 1 in full. Width transitions in lockstep with the rail
           expansion. */
        .trysec-rail[data-mode="minimal"] .trysec-rail-num,
        .trysec-rail[data-mode="full"] .trysec-rail-num {
          display: inline-block;
          flex: 0 0 auto;
          font-size: 11px;
          /* Weight 500 (not 600) so the number sits quietly behind the
             label. The number is a navigation reference, not a primary
             scan target — making it match the inactive label weight
             keeps the label as the optical lead. */
          font-weight: 500;
          font-variant-numeric: tabular-nums;
          letter-spacing: 0;
          color: ${C.textLo};
          white-space: nowrap;
          overflow: hidden;
          transition: width 280ms cubic-bezier(0.22, 1, 0.36, 1),
                      opacity 220ms ease,
                      color 180ms ease;
        }
        .trysec-rail[data-mode="minimal"] .trysec-rail-num {
          width: 0;
          opacity: 0;
        }
        .trysec-rail[data-mode="full"] .trysec-rail-num {
          width: 32px;
          opacity: 1;
        }
        .trysec-rail[data-mode="full"] .trysec-rail-btn[data-active="true"] .trysec-rail-num {
          color: ${C.textHi};
        }

        /* Label — flex-grow keeps it filling whatever space is available
           (zero in minimal, ~120px in full). Single-line + ellipsis on
           overflow; the full label is in the button's title tooltip
           for hover discovery. Visibility is driven purely by opacity so
           the text does not appear as a sliver at intermediate widths
           during the transition. */
        .trysec-rail[data-mode="minimal"] .trysec-rail-label,
        .trysec-rail[data-mode="full"] .trysec-rail-label {
          flex: 1 1 auto;
          min-width: 0;
          font-size: 13px;
          line-height: 1.4;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: ${C.textMd};
          transition: opacity 220ms ease,
                      color 180ms ease,
                      font-weight 180ms ease;
        }
        .trysec-rail[data-mode="minimal"] .trysec-rail-label { opacity: 0; }
        .trysec-rail[data-mode="full"]    .trysec-rail-label { opacity: 1; }
        .trysec-rail[data-mode="full"] .trysec-rail-btn[data-active="true"] .trysec-rail-label {
          color: ${C.textHi};
          font-weight: 600;
        }

        /* ── Toggle button — top of the right rail. Same icon vocabulary
           as the left sidebar's toggle so the two read as a matched pair.
           Positioned at the rail's left edge in minimal mode (where it's
           the only thing wider than the 56px column allows) and inline at
           the top of the panel in full mode. ────────────────────────── */
        .trysec-rail-toggle {
          width: 28px;
          height: 28px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          color: ${C.textMd};
          padding: 0;
          margin-left: 10px;
          margin-bottom: 12px;
          transition: background 180ms ease, color 180ms ease;
        }
        .trysec-rail-toggle:hover {
          background: var(--tq-mute);
          color: ${C.textHi};
        }

        /* (Full mode styles live in the unified "/try4 modes" block above —
           the two modes share a single layout so the browser can
           interpolate every visual difference into one smart-animate
           transition.) */

        @media (max-width: 1200px) {
          /* Collapse to a single content column on tablet/mobile. The
             per-mode selectors above ([data-rail-mode="…"]) are more specific
             than a bare ".trysec-grid", so they must be re-stated here or the
             rail's column keeps reserving 200px while the rail itself is
             hidden — leaving a phantom gutter that squeezes the content. */
          .trysec-grid,
          .trysec-grid[data-rail-mode="always-open"],
          .trysec-grid[data-rail-mode="minimal"],
          .trysec-grid[data-rail-mode="full"] { grid-template-columns: minmax(0, 1fr); }
          .trysec-rail { display: none; }
        }
      `;
